/**
 * The selection overlay: outline, eight resize handles, rotate grip and a
 * dimension chip.
 *
 * Drawn in SCREEN space on top of the artboard rather than inside it, so the
 * handles stay a constant 8 px no matter the zoom — scaling them with the
 * canvas would make them unusable at 25%.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import type { Rect, ResizeHandle } from '../../design-builder.types';
import { HANDLE_CURSORS, RESIZE_HANDLES, handleOffset, roundGeometry } from '../../utils/design-geometry.utils';
import type { Viewport } from '../../utils/design-geometry.utils';

export interface HandlePointerEvent {
  handle: ResizeHandle;
  event: PointerEvent;
}

@Component({
  selector: 'app-design-selection-box',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  templateUrl: './design-selection-box.component.html',
  styleUrl: './design-selection-box.component.scss',
})
export class DesignSelectionBoxComponent {
  /** Selection bounds in DOCUMENT space. */
  readonly bounds = input.required<Rect>();
  readonly viewport = input.required<Viewport>();
  /** Multi-selections get an outline but no handles — resizing many nodes at
   *  once is not something this builder does yet. */
  readonly multiple = input<boolean>(false);
  readonly selectionCount = input<number>(1);
  /** True only after the selected items have become a real GROUP node. */
  readonly grouped = input<boolean>(false);
  /** The screen: its box is derived, so it gets an outline but no handles. */
  readonly fixed = input<boolean>(false);
  /** Hide handles mid-gesture so they do not flicker under the cursor. */
  readonly interacting = input<boolean>(false);
  readonly rotation = input<number>(0);

  readonly resizeStart = output<HandlePointerEvent>();
  readonly rotateStart = output<PointerEvent>();
  readonly groupRequested = output<void>();

  readonly handles = RESIZE_HANDLES;

  /** Selection bounds converted to screen px. */
  readonly screenRect = computed<Rect>(() => {
    const { zoom, panX, panY } = this.viewport();
    const rect = this.bounds();
    return {
      x: rect.x * zoom + panX,
      y: rect.y * zoom + panY,
      width: rect.width * zoom,
      height: rect.height * zoom,
    };
  });

  readonly boxStyles = computed<Record<string, string>>(() => {
    const rect = this.screenRect();
    const styles: Record<string, string> = {
      left: `${rect.x}px`,
      top: `${rect.y}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
    };
    if (this.rotation()) {
      styles['transform'] = `rotate(${this.rotation()}deg)`;
      styles['transform-origin'] = 'center center';
    }
    return styles;
  });

  readonly showHandles = computed(() => !this.multiple() && !this.interacting() && !this.fixed());

  readonly label = computed(() => {
    if (this.multiple()) return `${this.selectionCount()} selected - group to resize`;
    const rect = this.bounds();
    const size = `${roundGeometry(rect.width)} × ${roundGeometry(rect.height)}`;
    return this.fixed() ? `${size} · fixed` : size;
  });

  onGroupClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.groupRequested.emit();
  }

  handleStyles(handle: ResizeHandle): Record<string, string> {
    const offset = handleOffset(handle);
    return {
      left: `${offset.x * 100}%`,
      top: `${offset.y * 100}%`,
      cursor: HANDLE_CURSORS[handle],
    };
  }

  onHandlePointerDown(handle: ResizeHandle, event: PointerEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.resizeStart.emit({ handle, event });
  }

  onRotatePointerDown(event: PointerEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.rotateStart.emit(event);
  }
}
